import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Upload, Save, X, Users, Archive, RotateCcw, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEPARTMENTS = ['Sales', 'Marketing', 'Operations', 'Warehouse', 'People', 'Facilities', 'Finance', 'Drivers'];

const PAGE_PERMISSIONS = [
  { id: 'vehicles', label: 'Vehicles' },
  { id: 'driver_schedules', label: 'Create/View Schedules' },
  { id: 'submitted_logs', label: 'Submitted Logs' },
  { id: 'driver_kpis', label: 'Reports & KPIs' },
  { id: 'warehouse_overview', label: 'Warehouse Overview' },
  { id: 'health_safety', label: 'Health & Safety' },
  { id: 'timesheets', label: 'Timesheets' },
  { id: 'finance_timesheets', label: 'Timesheets Review' },
  { id: 'employees', label: 'Employees' },
  { id: 'roadmap', label: '2026 Roadmap' },
  { id: 'impact_stats', label: 'Update Impact Stats' },
  { id: 'impact_builder', label: 'Impact Page Builder' },
  { id: 'announcements', label: 'Announcements' },
  { id: 'newsletters', label: 'Newsletters' },
  { id: 'surveys', label: 'Surveys' },
  { id: 'email_builder', label: 'Email Management' },
  { id: 'settings', label: 'Settings' }
];

const Employees = ({ user }) => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('active'); // 'active' or 'archived'
  const [showDialog, setShowDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [resetPasswordEmployee, setResetPasswordEmployee] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [managerDepartments, setManagerDepartments] = useState([]); // Departments the manager can manage
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    email: '',
    start_date: '',
    holiday_entitlement_days: 28,
    contracted_hours_per_week: 40,
    contracted_days_per_week: 5,
    default_start_time: '09:00',
    default_finish_time: '17:00',
    department: 'Warehouse',
    job_title: '',
    pin: '',
    is_manager: false,
    managed_departments: [],
    page_permissions: [],
    reports_to: ''
  });

  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager';
  const canManageEmployees = isAdmin || isManager;
  const pageTitle = isAdmin ? 'Employees' : 'My Team';

  // Get the departments available for selection (all for admin, managed only for managers)
  const availableDepartments = isAdmin ? DEPARTMENTS : managerDepartments;

  useEffect(() => {
    fetchEmployees();
  }, [viewMode]);

  // Fetch manager's departments when component mounts (for managers only)
  useEffect(() => {
    if (isManager && managerDepartments.length === 0) {
      fetchManagerDepartments();
    }
  }, [isManager]);

  const fetchManagerDepartments = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/employee/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const departments = data.managed_departments || [data.department];
        const filteredDepts = departments.filter(d => d);
        setManagerDepartments(filteredDepts);
        // Set default department to first managed department
        if (filteredDepts.length > 0) {
          setFormData(prev => ({ ...prev, department: filteredDepts[0] }));
        }
      } else {
        console.error('Failed to fetch manager departments:', res.status);
      }
    } catch (error) {
      console.error('Error fetching manager departments:', error);
    }
  };


  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const includeArchived = viewMode === 'archived';
      const endpoint = isAdmin 
        ? `${BACKEND_URL}/api/employees/all?include_archived=${includeArchived}`
        : `${BACKEND_URL}/api/employees/my-team?include_archived=${includeArchived}`;
      
      const res = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) {
        throw new Error('Failed to fetch employees');
      }
      
      const data = await res.json();
      // Filter based on view mode
      const filteredData = viewMode === 'archived' 
        ? data.filter(emp => emp.is_archived === true)
        : data.filter(emp => emp.is_archived !== true);
      setEmployees(filteredData);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.first_name || !formData.last_name || !formData.email) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const url = editingEmployee 
        ? `${BACKEND_URL}/api/employees/${editingEmployee.id}`
        : `${BACKEND_URL}/api/employees`;
      
      const payload = {
        ...formData,
        // If manager, use managed_departments array, otherwise empty array
        managed_departments: formData.is_manager ? formData.managed_departments : [],
        page_permissions: formData.is_manager ? formData.page_permissions : [],
        department: formData.department
      };

      const response = await fetch(url, {
        method: editingEmployee ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        let errorMessage = 'Failed to save employee';
        try {
          const error = await response.json();
          errorMessage = error.detail || errorMessage;
        } catch (e) {
          // If parsing JSON fails, use default message
        }
        throw new Error(errorMessage);
      }

      toast.success(editingEmployee ? 'Employee updated successfully' : 'Employee created successfully');
      setShowDialog(false);
      setEditingEmployee(null);
      resetForm();
      fetchEmployees();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      first_name: employee.first_name || '',
      last_name: employee.last_name || '',
      date_of_birth: employee.date_of_birth || '',
      start_date: employee.start_date || '',
      email: employee.email || '',
      holiday_entitlement_days: employee.holiday_entitlement_days || 28,
      contracted_hours_per_week: employee.contracted_hours_per_week || 40,
      contracted_days_per_week: employee.contracted_days_per_week || 5,
      default_start_time: employee.default_start_time || '09:00',
      default_finish_time: employee.default_finish_time || '17:00',
      department: employee.department || 'Warehouse',
      job_title: employee.job_title || '',
      pin: employee.pin || '',
      is_manager: employee.is_manager || false,
      managed_departments: employee.managed_departments || [],
      page_permissions: employee.page_permissions || [],
      reports_to: employee.reports_to || ''
    });
    setShowDialog(true);
  };

  const handleArchive = async (employeeId) => {
    if (!window.confirm('Are you sure you want to archive this employee? Their data will be preserved but they will not appear in active employee lists.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/employees/${employeeId}/archive`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to archive employee');
      }

      toast.success('Employee archived successfully');
      fetchEmployees();
    } catch (error) {
      toast.error('Failed to archive employee');
    }
  };

  const handleUnarchive = async (employeeId) => {
    if (!window.confirm('Are you sure you want to restore this employee to active status?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/employees/${employeeId}/unarchive`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to unarchive employee');
      }

      toast.success('Employee restored successfully');
      fetchEmployees();
    } catch (error) {
      toast.error('Failed to restore employee');
    }
  };

  const handleDelete = async (employeeId) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/employees/${employeeId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to delete employee');
      }

      toast.success('Employee deleted successfully');
      fetchEmployees();
    } catch (error) {
      toast.error('Failed to delete employee');
    }
  };


  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/employees/csv-template`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to download template');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'employee_import_template.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Template downloaded successfully');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleImportCSV = async () => {
    if (!importFile) {
      toast.error('Please select a CSV file');
      return;
    }

    setImporting(true);
    setImportResults(null);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await fetch(`${BACKEND_URL}/api/employees/import-csv`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to import employees');
      }

      setImportResults(data);
      
      if (data.imported_count > 0) {
        toast.success(`Successfully imported ${data.imported_count} employee(s)`);
        fetchEmployees();
      }
      
      if (data.error_count > 0) {
        toast.warning(`${data.error_count} row(s) had errors. Check the results below.`);
      }

    } catch (error) {
      toast.error(error.message || 'Failed to import employees');
    } finally {
      setImporting(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetPassword || resetPassword.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/employees/${resetPasswordEmployee.id}/reset-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password: resetPassword })
      });

      if (!response.ok) {
        throw new Error('Failed to reset password');
      }

      toast.success(`Password reset successfully. ${resetPasswordEmployee.first_name} will be prompted to set a new password on next login.`);
      setShowResetPasswordDialog(false);
      setResetPasswordEmployee(null);
      setResetPassword('');
    } catch (error) {
      toast.error(error.message || 'Failed to reset password');
    }
  };


  const openAddDialog = () => {
    setEditingEmployee(null);
    resetForm();
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      date_of_birth: '',
      start_date: '',
      email: '',
      holiday_entitlement_days: 28,
      default_start_time: '09:00',
      default_finish_time: '17:00',
      department: 'Warehouse',
      job_title: '',
      pin: '',
      is_manager: false,
      managed_departments: [],
      page_permissions: []
    });
  };

  const toggleDepartment = (dept) => {
    setFormData(prev => ({
      ...prev,
      managed_departments: prev.managed_departments.includes(dept)
        ? prev.managed_departments.filter(d => d !== dept)
        : [...prev.managed_departments, dept]
    }));
  };

  const togglePermission = (permission) => {
    setFormData(prev => ({
      ...prev,
      page_permissions: prev.page_permissions.includes(permission)
        ? prev.page_permissions.filter(p => p !== permission)
        : [...prev.page_permissions, permission]
    }));
  };

  return (
    <div className="overflow-x-hidden" data-testid="employees-container">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
        <div className="flex flex-col gap-4">
          <Button onClick={() => navigate('/admin/dashboard')} variant="ghost" size="sm" className="self-start">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{pageTitle}</h1>
            <p className="text-sm text-gray-500 mt-1">
              {canManageEmployees ? 'Manage your team members' : 'View your team members'}
            </p>
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-1">
            <button
              onClick={() => setViewMode('active')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'active'
                  ? 'bg-white text-emerald-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Active
            </button>
            <button
              onClick={() => setViewMode('archived')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'archived'
                  ? 'bg-white text-orange-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Archived
            </button>
          </div>

          {canManageEmployees && viewMode === 'active' && (
            <div className="flex gap-2">
              {isAdmin && (
                <Button
                  onClick={() => setShowImportDialog(true)}
                  variant="outline"
                  className="border-teal-600 text-teal-600 hover:bg-teal-50"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Import CSV
                </Button>
              )}
              <Button
                onClick={openAddDialog}
                className="bg-teal-600 hover:bg-teal-700 w-full sm:w-auto"
                data-testid="add-employee-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Employee
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Employees Table */}
      {loading ? (
        <div className="text-center py-12">
          <p className="text-gray-500">Loading employees...</p>
        </div>
      ) : employees.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-md">
          <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg mb-4">No employees found</p>
          {canManageEmployees && (
            <Button onClick={openAddDialog} className="bg-teal-600 hover:bg-teal-700">
              <Plus className="w-4 h-4 mr-2" />
              Add First Employee
            </Button>
          )}
        </div>
      ) : (
        <>
          {/* Desktop Table View - Hidden on mobile */}
          <div className="hidden lg:block bg-white rounded-xl shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Department
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Job Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Status
                    </th>
                    {canManageEmployees && (
                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {employees.map((employee) => (
                    <tr key={employee.id} className="hover:bg-gray-50" data-testid={`employee-row-${employee.id}`}>
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">
                          {employee.first_name} {employee.last_name}
                        </div>
                        {employee.is_manager && (
                          <span className="inline-block mt-1 px-2 py-0.5 text-xs font-semibold text-purple-700 bg-purple-100 rounded">
                            Manager
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-gray-900">{employee.email}</td>
                      <td className="px-6 py-4 text-gray-900">
                        {employee.is_manager && employee.managed_departments?.length > 0 ? (
                          <div>
                            <div className="font-medium">{employee.department}</div>
                            <div className="text-xs text-gray-500 mt-1">
                              Manages: {employee.managed_departments.join(', ')}
                            </div>
                          </div>
                        ) : (
                          employee.department
                        )}
                      </td>
                      <td className="px-6 py-4 text-gray-900">{employee.job_title}</td>
                      <td className="px-6 py-4">
                        <span className="px-3 py-1 text-xs font-semibold text-green-700 bg-green-100 rounded-full">
                          Active
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex gap-2 justify-end">
                          <Button
                            onClick={() => navigate(`/admin/employees/${employee.id}`)}
                            size="sm"
                            className="bg-teal-600 hover:bg-teal-700 text-white"
                            data-testid={`view-profile-${employee.id}`}
                          >
                            View Profile
                          </Button>
                          {canManageEmployees && (
                            <>
                              {!employee.is_archived && (
                                <>
                                  <Button
                                    onClick={() => handleEdit(employee)}
                                    size="sm"
                                    variant="outline"
                                    data-testid={`edit-employee-${employee.id}`}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    onClick={() => {
                                      setResetPasswordEmployee(employee);
                                      setShowResetPasswordDialog(true);
                                    }}
                                    size="sm"
                                    variant="outline"
                                    className="border-purple-500 text-purple-600 hover:bg-purple-50"
                                    title="Reset Password"
                                  >
                                    🔑
                                  </Button>
                                  {/* Managers can only archive non-managers */}
                                  {(isAdmin || !employee.is_manager) && (
                                    <Button
                                      onClick={() => handleArchive(employee.id)}
                                      size="sm"
                                      variant="outline"
                                      className="border-orange-500 text-orange-600 hover:bg-orange-50"
                                      data-testid={`archive-employee-${employee.id}`}
                                    >
                                      <Archive className="w-4 h-4" />
                                    </Button>
                                  )}
                                </>
                              )}
                              {employee.is_archived && (isAdmin || !employee.is_manager) && (
                                <Button
                                  onClick={() => handleUnarchive(employee.id)}
                                  size="sm"
                                  variant="outline"
                                  className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                                  data-testid={`unarchive-employee-${employee.id}`}
                                >
                                  <RotateCcw className="w-4 h-4" />
                                </Button>
                              )}
                              {/* Only admins can permanently delete */}
                              {isAdmin && (
                                <Button
                                  onClick={() => handleDelete(employee.id)}
                                  size="sm"
                                  variant="outline"
                                  className="border-red-500 text-red-600 hover:bg-red-50"
                                  data-testid={`delete-employee-${employee.id}`}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Card View - Visible only on mobile/tablet */}
          <div className="lg:hidden space-y-4">
            {employees.map((employee) => (
              <div
                key={employee.id}
                className="bg-white rounded-xl shadow-md p-4"
                data-testid={`employee-card-${employee.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg">
                      {employee.first_name} {employee.last_name}
                    </h3>
                    {employee.is_manager && (
                      <span className="inline-block mt-1 px-2 py-0.5 text-xs font-semibold text-purple-700 bg-purple-100 rounded">
                        Manager
                      </span>
                    )}
                  </div>
                  <span className="px-3 py-1 text-xs font-semibold text-green-700 bg-green-100 rounded-full">
                    Active
                  </span>
                </div>

                <div className="space-y-2 mb-4 text-sm">
                  <div>
                    <span className="text-gray-500">Email:</span>
                    <span className="ml-2 text-gray-900 break-all">{employee.email}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Department:</span>
                    <span className="ml-2 text-gray-900">{employee.department}</span>
                  </div>
                  {employee.is_manager && employee.managed_departments?.length > 0 && (
                    <div>
                      <span className="text-gray-500">Manages:</span>
                      <span className="ml-2 text-gray-900">{employee.managed_departments.join(', ')}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-500">Job Title:</span>
                    <span className="ml-2 text-gray-900">{employee.job_title}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  {/* Row 1: View Profile | Edit */}
                  <Button
                    onClick={() => navigate(`/admin/employees/${employee.id}`)}
                    className="bg-teal-600 hover:bg-teal-700 text-white py-3 text-sm font-medium"
                    data-testid={`view-profile-mobile-${employee.id}`}
                  >
                    View Profile
                  </Button>
                  {canManageEmployees && !employee.is_archived && (
                    <Button
                      onClick={() => handleEdit(employee)}
                      variant="outline"
                      className="py-3 text-sm font-medium"
                      data-testid={`edit-employee-mobile-${employee.id}`}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                  )}
                  {canManageEmployees && employee.is_archived && (isAdmin || !employee.is_manager) && (
                    <Button
                      onClick={() => handleUnarchive(employee.id)}
                      variant="outline"
                      className="border-emerald-500 text-emerald-600 hover:bg-emerald-50 py-3 text-sm font-medium"
                      data-testid={`unarchive-employee-mobile-${employee.id}`}
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Unarchive
                    </Button>
                  )}
                  
                  {/* Row 2: Archive | Delete */}
                  {canManageEmployees && !employee.is_archived && (isAdmin || !employee.is_manager) && (
                    <Button
                      onClick={() => handleArchive(employee.id)}
                      variant="outline"
                      className="border-orange-500 text-orange-600 hover:bg-orange-50 py-3 text-sm font-medium"
                      data-testid={`archive-employee-mobile-${employee.id}`}
                    >
                      <Archive className="w-4 h-4 mr-1" />
                      Archive
                    </Button>
                  )}
                  {isAdmin && (
                    <Button
                      onClick={() => handleDelete(employee.id)}
                      variant="outline"
                      className="border-red-500 text-red-600 hover:bg-red-50 py-3 text-sm font-medium"
                      data-testid={`delete-employee-mobile-${employee.id}`}
                    >
                      <X className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEmployee ? 'Edit Employee' : 'Add Employee'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* First Name & Last Name */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">First Name *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="last_name">Last Name *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Date of Birth */}
            <div>
              <Label htmlFor="date_of_birth">Date of Birth</Label>
              <Input
                id="date_of_birth"
                type="date"
                value={formData.date_of_birth}
                onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                className="mt-1"
              />
            </div>

            {/* Start Date */}
            <div>
              <Label htmlFor="start_date">Start Date (Employment) *</Label>
              <Input
                id="start_date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Used for long service bonus (+1 day/year, max 3)</p>
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email">Personal Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Used for Xero integration and payslips</p>
            </div>

            {/* Work Email */}
            <div>
              <Label htmlFor="work_email">Work Email</Label>
              <Input
                id="work_email"
                type="email"
                value={formData.work_email || ''}
                onChange={(e) => setFormData({...formData, work_email: e.target.value})}
                className="mt-1"
                placeholder="work@reuseabox.co.uk"
              />
              <p className="text-xs text-gray-500 mt-1">Used for notifications and email digests</p>
            </div>

            {/* Annual Leave Allowance */}
            <div>
              <Label htmlFor="holiday_entitlement_days">Annual Leave Allowance (Days)</Label>
              <Input
                id="holiday_entitlement_days"
                type="number"
                step="0.5"
                value={formData.holiday_entitlement_days}
                onChange={(e) => setFormData({...formData, holiday_entitlement_days: parseFloat(e.target.value) || 0})}
                className="mt-1"
              />
            </div>

            {/* Contracted Hours Per Week */}
            <div>
              <Label htmlFor="contracted_hours_per_week">Contracted Hours Per Week</Label>
              <Input
                id="contracted_hours_per_week"
                type="number"
                step="0.5"
                min="0"
                max="168"
                placeholder="e.g., 40"
                value={formData.contracted_hours_per_week || ''}
                onChange={(e) => setFormData({...formData, contracted_hours_per_week: parseFloat(e.target.value) || 40})}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Used to calculate overtime and leave entitlement</p>
            </div>

            {/* Contracted Days Per Week */}
            <div>
              <Label htmlFor="contracted_days_per_week">Contracted Days Per Week</Label>
              <Input
                id="contracted_days_per_week"
                type="number"
                step="0.5"
                min="0"
                max="7"
                placeholder="e.g., 5"
                value={formData.contracted_days_per_week || ''}
                onChange={(e) => setFormData({...formData, contracted_days_per_week: parseFloat(e.target.value) || 5})}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Working days per week (default: 5, use 3.5 for part-time)</p>
            </div>

            {/* Default Start & Finish Time */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="default_start_time">Default Start Time</Label>
                <Input
                  id="default_start_time"
                  type="time"
                  value={formData.default_start_time || ''}
                  onChange={(e) => setFormData({...formData, default_start_time: e.target.value})}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">Used to calculate late start (5 min grace period)</p>
              </div>
              <div>
                <Label htmlFor="default_finish_time">Default Finish Time</Label>
                <Input
                  id="default_finish_time"
                  type="time"
                  value={formData.default_finish_time || ''}
                  onChange={(e) => setFormData({...formData, default_finish_time: e.target.value})}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">Used to calculate early/late finish</p>
              </div>
            </div>

            {/* Department & Job Title */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="department">Department</Label>
                <select
                  id="department"
                  value={formData.department}
                  onChange={(e) => setFormData({...formData, department: e.target.value})}
                  className="mt-1 w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  {availableDepartments.map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
                {isManager && (
                  <p className="text-xs text-gray-500 mt-1">You can only add employees to departments you manage</p>
                )}
              </div>
              <div>
                <Label htmlFor="job_title">Job Title</Label>
                <Input
                  id="job_title"
                  value={formData.job_title}
                  onChange={(e) => setFormData({...formData, job_title: e.target.value})}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Clock In/Out PIN */}
            <div>
              <Label htmlFor="pin">Clock In/Out PIN (4 digits)</Label>
              <Input
                id="pin"
                type="text"
                maxLength="4"
                placeholder="1234"
                value={formData.pin}
                onChange={(e) => setFormData({...formData, pin: e.target.value.replace(/\D/g, '')})}
                className="mt-1"
              />
            </div>

            {/* Manager Checkbox - Only admins can create/edit managers */}
            {isAdmin && (
              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="is_manager"
                  checked={formData.is_manager}
                  onChange={(e) => setFormData({...formData, is_manager: e.target.checked})}
                  className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                />
                <Label htmlFor="is_manager" className="cursor-pointer font-semibold">This person is a manager</Label>
              </div>
            )}

            {/* Managed Departments - Show when manager checkbox is ticked (admin only) */}
            {isAdmin && formData.is_manager && (
              <>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <Label className="font-semibold mb-3 block">Which departments does this manager oversee?</Label>
                  <div className="space-y-2">
                    {DEPARTMENTS.map(dept => (
                      <div key={dept} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id={`dept-${dept}`}
                          checked={formData.managed_departments.includes(dept)}
                          onChange={() => toggleDepartment(dept)}
                          className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                        />
                        <Label htmlFor={`dept-${dept}`} className="cursor-pointer">{dept}</Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <Label className="font-semibold mb-3 block">Which pages can this manager access?</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {PAGE_PERMISSIONS.map(perm => (
                      <div key={perm.id} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id={`perm-${perm.id}`}
                          checked={formData.page_permissions.includes(perm.id)}
                          onChange={() => togglePermission(perm.id)}
                          className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                        />
                        <Label htmlFor={`perm-${perm.id}`} className="cursor-pointer text-sm">{perm.label}</Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Reports To - Manager Hierarchy */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <Label htmlFor="reports_to" className="font-semibold mb-3 block">Reports To (Line Manager)</Label>
                  <p className="text-sm text-gray-600 mb-3">Select who this manager reports to for holiday approvals and oversight.</p>
                  <select
                    id="reports_to"
                    value={formData.reports_to}
                    onChange={(e) => setFormData({...formData, reports_to: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="">No manager (Reports to admin)</option>
                    {employees
                      .filter(emp => emp.is_manager && emp.id !== formData.id)
                      .map(manager => (
                        <option key={manager.id} value={manager.id}>
                          {manager.first_name} {manager.last_name} - {manager.department}
                        </option>
                      ))}
                  </select>
                </div>
              </>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              onClick={() => {
                setShowDialog(false);
                setEditingEmployee(null);
                resetForm();
              }}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {editingEmployee ? 'Update' : 'Create'} Employee
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import CSV Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Import Employees from CSV</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2">📋 Instructions</h3>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>Download the CSV template below</li>
                <li>Fill in employee details (required fields: first_name, last_name, email)</li>
                <li>Save the file and upload it here</li>
                <li>Review any errors and re-upload if needed</li>
              </ol>
            </div>

            <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium">CSV Template</p>
                <p className="text-sm text-gray-600">Download template with example data</p>
              </div>
              <Button
                onClick={handleDownloadTemplate}
                variant="outline"
                className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
              >
                <Upload className="w-4 h-4 mr-2" />
                Download Template
              </Button>
            </div>

            <div>
              <Label>Select CSV File</Label>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => {
                  setImportFile(e.target.files[0]);
                  setImportResults(null);
                }}
                className="mt-2 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
              />
              {importFile && (
                <p className="text-sm text-gray-600 mt-2">Selected: {importFile.name}</p>
              )}
            </div>

            {importResults && (
              <div className={`p-4 rounded-lg border ${importResults.error_count > 0 ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'}`}>
                <h3 className="font-semibold mb-2">Import Results</h3>
                <div className="space-y-1 text-sm">
                  <p className="text-green-700">✅ Successfully imported: {importResults.imported_count} employee(s)</p>
                  {importResults.error_count > 0 && (
                    <>
                      <p className="text-red-700">❌ Errors: {importResults.error_count} row(s)</p>
                      <div className="mt-2 max-h-40 overflow-y-auto bg-white p-2 rounded border">
                        {importResults.errors?.map((error, idx) => (
                          <p key={idx} className="text-xs text-red-600 mb-1">{error}</p>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowImportDialog(false);
                setImportFile(null);
                setImportResults(null);
              }}
            >
              {importResults ? 'Close' : 'Cancel'}
            </Button>
            {!importResults && (
              <Button
                onClick={handleImportCSV}
                disabled={!importFile || importing}
                className="bg-teal-600 hover:bg-teal-700"
              >
                {importing ? 'Importing...' : 'Import Employees'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPasswordDialog} onOpenChange={setShowResetPasswordDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {resetPasswordEmployee && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <p className="font-semibold text-purple-900">
                  {resetPasswordEmployee.first_name} {resetPasswordEmployee.last_name}
                </p>
                <p className="text-sm text-purple-700">
                  {resetPasswordEmployee.email}
                </p>
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-900">
                Set a temporary password. The employee will be prompted to create a new password on their next login.
              </p>
            </div>

            <div>
              <Label>New Temporary Password</Label>
              <Input
                type="text"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                placeholder="Enter temporary password (min 4 characters)"
                className="mt-2"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowResetPasswordDialog(false);
                setResetPasswordEmployee(null);
                setResetPassword('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleResetPassword}
              className="bg-purple-600 hover:bg-purple-700"
              disabled={!resetPassword || resetPassword.length < 4}
            >
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Employees;
